import * as React from "react"
import { Search, SlidersHorizontal } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { PROPERTY_TYPES, REGIONS } from "@/lib/constants"
import { useLocale } from "@/lib/i18n"
import type { PropertySearch } from "@/lib/types"

const propertyTypeMessage = {
  residential: "residential",
  commercial: "commercial",
  land: "land",
  investment: "investment",
  "holiday-rental": "holidayRental",
} as const

const numberValue = (form: FormData, key: string) => {
  const raw = String(form.get(key) ?? "").trim()
  if (!raw) return undefined
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : undefined
}

export function searchFromForm(form: FormData): PropertySearch {
  const value = (key: string) => String(form.get(key) ?? "").trim()
  const sort = value("sort")
  return {
    transaction:
      value("transaction") === "rent"
        ? "rent"
        : value("transaction") === "buy"
          ? "buy"
          : undefined,
    type:
      value("type") === "all" || !value("type")
        ? undefined
        : (value("type") as PropertySearch["type"]),
    keywords: value("keywords") || undefined,
    reference: value("reference") || undefined,
    region: value("region") || undefined,
    locality: value("locality") || undefined,
    minPrice: numberValue(form, "minPrice"),
    maxPrice: numberValue(form, "maxPrice"),
    bedrooms: numberValue(form, "bedrooms"),
    bathrooms: numberValue(form, "bathrooms"),
    minFloorArea: numberValue(form, "minFloorArea"),
    minLandArea: numberValue(form, "minLandArea"),
    nearCbd: form.get("nearCbd") === "on" || undefined,
    waterfront: form.get("waterfront") === "on" || undefined,
    sort: sort === "price-asc" || sort === "price-desc" ? sort : "newest",
    page: 1,
  }
}

export function PropertySearchForm({
  defaults,
  onSearch,
  compact = false,
  submitLabel,
}: {
  defaults?: PropertySearch
  onSearch: (search: PropertySearch) => void | Promise<void>
  compact?: boolean
  submitLabel?: string
}) {
  const { t } = useLocale()
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    void onSearch(searchFromForm(new FormData(event.currentTarget)))
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className={compact ? "grid gap-3" : "grid gap-3 sm:grid-cols-2"}>
        <Field label={t("lookingTo")} htmlFor="transaction">
          <select
            id="transaction"
            name="transaction"
            defaultValue={defaults?.transaction ?? ""}
            className="field-input"
          >
            <option value="">{t("buyOrRent")}</option>
            <option value="buy">{t("buy")}</option>
            <option value="rent">{t("rent")}</option>
          </select>
        </Field>
        <Field label={t("propertyType")} htmlFor="type">
          <select
            id="type"
            name="type"
            defaultValue={defaults?.type ?? "all"}
            className="field-input"
          >
            <option value="all">{t("everyType")}</option>
            {PROPERTY_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {t(propertyTypeMessage[type.value])}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("region")} htmlFor="region">
          <select
            id="region"
            name="region"
            defaultValue={defaults?.region ?? ""}
            className="field-input"
          >
            <option value="">{t("everyRegion")}</option>
            {REGIONS.map((region) => (
              <option key={region} value={region}>
                {region}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("keywords")} htmlFor="keywords">
          <Input
            id="keywords"
            name="keywords"
            defaultValue={defaults?.keywords}
            placeholder={t("keywordsPlaceholder")}
            className="field-input"
          />
        </Field>
      </div>

      {!compact ? (
        <Accordion>
          <AccordionItem value="refine" className="border-y">
            <AccordionTrigger className="rounded-none py-3 font-heading text-sm font-bold uppercase">
              <span className="flex items-center gap-2">
                <SlidersHorizontal className="size-4" />
                {t("refineField")}
              </span>
            </AccordionTrigger>
            <AccordionContent className="grid gap-4 pt-3 sm:grid-cols-2 xl:grid-cols-3">
              <Field label={t("listingReference")} htmlFor="reference">
                <Input
                  id="reference"
                  name="reference"
                  defaultValue={defaults?.reference}
                  placeholder="HP-104"
                  className="field-input"
                />
              </Field>
              <Field label={t("locality")} htmlFor="locality">
                <Input
                  id="locality"
                  name="locality"
                  defaultValue={defaults?.locality}
                  placeholder={t("neighborhood")}
                  className="field-input"
                />
              </Field>
              <Field label={t("minimumPrice")} htmlFor="minPrice">
                <Input
                  id="minPrice"
                  name="minPrice"
                  type="number"
                  min="0"
                  defaultValue={defaults?.minPrice}
                  className="field-input"
                />
              </Field>
              <Field label={t("maximumPrice")} htmlFor="maxPrice">
                <Input
                  id="maxPrice"
                  name="maxPrice"
                  type="number"
                  min="0"
                  defaultValue={defaults?.maxPrice}
                  className="field-input"
                />
              </Field>
              <Field label={t("minimumBedrooms")} htmlFor="bedrooms">
                <Input
                  id="bedrooms"
                  name="bedrooms"
                  type="number"
                  min="0"
                  defaultValue={defaults?.bedrooms}
                  className="field-input"
                />
              </Field>
              <Field label={t("minimumBathrooms")} htmlFor="bathrooms">
                <Input
                  id="bathrooms"
                  name="bathrooms"
                  type="number"
                  min="0"
                  defaultValue={defaults?.bathrooms}
                  className="field-input"
                />
              </Field>
              <Field label={t("minimumInterior")} htmlFor="minFloorArea">
                <Input
                  id="minFloorArea"
                  name="minFloorArea"
                  type="number"
                  min="0"
                  defaultValue={defaults?.minFloorArea}
                  className="field-input"
                />
              </Field>
              <Field label={t("minimumLand")} htmlFor="minLandArea">
                <Input
                  id="minLandArea"
                  name="minLandArea"
                  type="number"
                  min="0"
                  defaultValue={defaults?.minLandArea}
                  className="field-input"
                />
              </Field>
              <Field label={t("sortBy")} htmlFor="sort">
                <select
                  id="sort"
                  name="sort"
                  defaultValue={defaults?.sort ?? "newest"}
                  className="field-input"
                >
                  <option value="newest">{t("newestFirst")}</option>
                  <option value="price-asc">{t("priceLowHigh")}</option>
                  <option value="price-desc">{t("priceHighLow")}</option>
                </select>
              </Field>
              <div className="flex flex-wrap items-end gap-5 pb-2 sm:col-span-2 xl:col-span-3">
                <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                  <Checkbox name="nearCbd" defaultChecked={defaults?.nearCbd} />
                  {t("nearTown")}
                </label>
                <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                  <Checkbox
                    name="waterfront"
                    defaultChecked={defaults?.waterfront}
                  />
                  {t("waterfront")}
                </label>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ) : null}

      <Button type="submit" size="lg" className="field-button w-full">
        <Search />
        {submitLabel ?? t("searchListings")}
      </Button>
    </form>
  )
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="field-label">
        {label}
      </Label>
      {children}
    </div>
  )
}
